import React, { useState, useEffect } from 'react';
import './StockTable.css';

function StockTable({ 
  stockData, 
  updateHomeStock, 
  updateTransitStock, 
  transferTransitToHome,
  fbaTransferDelay, 
  updateFbaTransferDelay,
  supplierTransitDelay,
  updateSupplierTransitDelay,
  supplierConfig
}) {
  const [sortField, setSortField] = useState('sku');
  const [sortDirection, setSortDirection] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingHomeStock, setEditingHomeStock] = useState(null);
  const [tempHomeStock, setTempHomeStock] = useState('');
  
  // États pour le popup de commande
  const [editingTransitStock, setEditingTransitStock] = useState(null);
  const [tempTransitStock, setTempTransitStock] = useState('');
  const [tempTransitDate, setTempTransitDate] = useState('');
  const [tempSupplierId, setTempSupplierId] = useState('');
  
  // États pour le popup de livraison
  const [deliveringTransitStock, setDeliveringTransitStock] = useState(null);
  const [deliveryDestination, setDeliveryDestination] = useState('fba-home');
  
  const [editingFbaDelay, setEditingFbaDelay] = useState(false);
  const [tempFbaDelay, setTempFbaDelay] = useState(fbaTransferDelay || 10);
  
  // Stock de sécurité identique à ReorderRecommendations
  const safetyStock = 8;
  
  // Pour débogage, afficher les données pour voir leur structure
  useEffect(() => {
    if (stockData && stockData.length > 0) {
      console.log("Échantillon de données:", stockData[0]);
    }
  }, [stockData]);

  // Si pas de données, afficher un message
  if (!stockData || stockData.length === 0) {
    return <p>Aucune donnée de stock disponible</p>;
  }

  // Fonction de tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filtrer les données selon le terme de recherche
  const filteredData = stockData.filter(item => {
    const term = searchTerm.toLowerCase();
    return (
      (item.sku && item.sku.toLowerCase().includes(term)) ||
      (item.productName && item.productName.toLowerCase().includes(term))
    );
  });

  // Trier les données
  const sortedData = [...filteredData].sort((a, b) => {
    let valueA = a[sortField];
    let valueB = b[sortField];
    
    // Convertir en nombres pour les champs numériques
    if (['quantity', 'fbmQuantity', 'fbaQuantity', 'homeQuantity', 'transitQuantity', 'dailySales'].includes(sortField)) {
      valueA = parseFloat(valueA) || 0;
      valueB = parseFloat(valueB) || 0;
    } else if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
    }
    
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Gestion de l'édition du stock à la maison
  const handleHomeStockEdit = (sku) => {
    const item = stockData.find(item => item.sku === sku);
    setEditingHomeStock(sku);
    setTempHomeStock(item.homeQuantity || 0);
  };

  const handleHomeStockSave = (sku) => {
    updateHomeStock(sku, tempHomeStock);
    setEditingHomeStock(null);
  };
  
  // Gestion de l'édition du stock en transit (commande)
  const handleTransitStockEdit = (sku) => {
    const item = stockData.find(item => item.sku === sku);
    
    // Récupérer la date de commande existante ou utiliser la date du jour
    let orderDate = new Date();
    if (item.transitOrderDate) {
      orderDate = new Date(item.transitOrderDate);
    }
    
    // Récupérer l'ID du fournisseur existant
    let supplierId = '';
    if (item.transitSupplierId) {
      supplierId = item.transitSupplierId;
    } else if (supplierConfig && supplierConfig.productSuppliers && supplierConfig.productSuppliers[sku]) {
      supplierId = supplierConfig.productSuppliers[sku];
    }
    
    setEditingTransitStock(sku);
    setTempTransitStock(item.transitQuantity || 0);
    setTempTransitDate(orderDate.toISOString().split('T')[0]); // Format YYYY-MM-DD
    setTempSupplierId(supplierId);
  };

  const handleTransitStockSave = (sku) => {
    updateTransitStock(sku, tempTransitStock, tempTransitDate, tempSupplierId);
    setEditingTransitStock(null);
  };
  
  // Gestion de la livraison du stock en transit
  const handleTransitDeliveryOpen = (sku) => {
    setDeliveringTransitStock(sku);
    setDeliveryDestination('fba-home'); // Option par défaut
  };
  
  const handleTransitDeliveryComplete = (sku) => {
    if (deliveryDestination === 'fba-home') {
      // Utiliser la nouvelle fonction qui gère tout en une seule transaction
      const success = transferTransitToHome(sku);
      
      if (success) {
        alert(`Stock transféré avec succès vers le stock à la maison`);
      }
    } else {
      // Pour l'option FBM, juste remettre à zéro
      updateTransitStock(sku, 0, null, null);
      alert(`Stock en transit réinitialisé. N'oubliez pas d'importer votre fichier FBM pour mettre à jour l'inventaire.`);
    }
    
    // Fermer le popup
    setDeliveringTransitStock(null);
  };

  // Gestion de l'édition du délai de transfert FBA
  const handleFbaDelayEdit = () => {
    setEditingFbaDelay(true);
    setTempFbaDelay(fbaTransferDelay);
  };

  const handleFbaDelaySave = () => {
    updateFbaTransferDelay(tempFbaDelay);
    setEditingFbaDelay(false);
  };
  
  // Obtenir le délai du fournisseur spécifique
  const getSupplierLeadTime = (supplierId) => {
    if (!supplierConfig || !supplierConfig.suppliers) return null;
    
    const supplier = supplierConfig.suppliers.find(s => s.id === parseInt(supplierId));
    return supplier ? supplier.leadTime : null;
  };
  
  // Calcule le nombre de jours restants avant que le stock en transit ne soit disponible
  const calculateTransitDaysRemaining = (item) => {
    if (!item.transitOrderDate || !item.transitSupplierId) return '-';
    
    const orderDate = new Date(item.transitOrderDate);
    const supplierLeadTime = getSupplierLeadTime(item.transitSupplierId);
    
    if (!supplierLeadTime) return 'Délai inconnu';
    
    const arrivalDate = new Date(orderDate);
    arrivalDate.setDate(orderDate.getDate() + supplierLeadTime);
    
    const today = new Date();
    const daysRemaining = Math.ceil((arrivalDate - today) / (1000 * 60 * 60 * 24));
    
    return daysRemaining > 0 ? daysRemaining : 'Arrivé';
  };

  return (
    <div className="stock-table-container">
      <h2>Inventaire actuel</h2>
      
      <div className="table-controls">
        <input
          type="text"
          placeholder="Rechercher par SKU ou nom du produit..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <div className="delays-control">
          <div className="delay-setting">
            <label>
              Délai de transfert FBA (jours): 
              {editingFbaDelay ? (
                <>
                  <input
                    type="number"
                    value={tempFbaDelay}
                    min="1"
                    onChange={(e) => setTempFbaDelay(e.target.value)}
                    className="delay-input"
                  />
                  <button onClick={handleFbaDelaySave} className="save-btn">✓</button>
                </>
              ) : (
                <>
                  <span className="delay-value">{fbaTransferDelay}</span>
                  <button onClick={handleFbaDelayEdit} className="edit-btn">✎</button>
                </>
              )}
            </label>
          </div>
          <div className="delay-setting">
            <span>Stock de sécurité: {safetyStock} jours</span>
          </div>
        </div>
      </div>
      
      <div className="table-wrapper">
        <table className="stock-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('sku')}>
                SKU {sortField === 'sku' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('quantity')}>
                Stock Total {sortField === 'quantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('fbmQuantity')}>
                Stock FBM {sortField === 'fbmQuantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('fbaQuantity')}>
                Stock FBA {sortField === 'fbaQuantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('homeQuantity')}>
                Stock FBA à la maison {sortField === 'homeQuantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th onClick={() => handleSort('transitQuantity')}>
                Stock en transit {sortField === 'transitQuantity' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th>
                Date de commande
              </th>
              <th>
                Jours avant arrivée
              </th>
              <th onClick={() => handleSort('dailySales')}>
                Ventes/jour {sortField === 'dailySales' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
              <th>Jours de stock</th>
              <th onClick={() => handleSort('productName')}>
                Produit {sortField === 'productName' && (sortDirection === 'asc' ? '▲' : '▼')}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, rowIndex) => {
              // Récupérer le SKU
              const sku = item.sku;
              
              // Calculer le nombre de jours de stock estimés
              // Utiliser 0.1 comme valeur par défaut uniquement si les ventes ne sont pas explicitement 0
              const originalSales = parseFloat(item.dailySales);
              const dailySales = originalSales === 0 ? 0 : (originalSales || 0.1);              
              // Stock FBM + FBA
              const fbmStock = parseInt(item.fbmQuantity || 0);
              const fbaStock = parseInt(item.fbaQuantity || 0);
              const fbmFbaStock = fbmStock + fbaStock;
              
              // Stock à la maison
              const homeStock = parseInt(item.homeQuantity) || 0;
              
              // Stock en transit
              const transitStock = parseInt(item.transitQuantity) || 0;
              
              // Stock total
              const totalStock = fbmFbaStock + homeStock + transitStock;
              
              // Calculer le nombre de jours avec stock actuel (FBM + FBA)
              const daysWithCurrentStock = dailySales > 0 
                ? Math.floor(fbmFbaStock / dailySales) 
                : 999;
              
              // Calculer spécifiquement les jours de stock FBA (pour les recommandations d'envoi)
              const daysWithFBAStock = dailySales > 0 && fbaStock > 0
                ? Math.floor(fbaStock / dailySales)
                : 0;

              // Calcul de la date d'envoi à Amazon avec la nouvelle logique
              const today = new Date();
              let amazonSendDate = null;
              let amazonSendOverdue = false;
              let canSendToAmazon = false;

              // Nouveaux seuils (identiques à ReorderRecommendations)
              const displayThreshold = fbaTransferDelay + safetyStock + 7; // délai + sécurité + préparation (25 jours)
              const criticalLevel = fbaTransferDelay + safetyStock; // délai + sécurité (18 jours)

              // 1. Vérifier s'il faut envoyer à Amazon (stock FBA < seuil d'affichage)
              const needToSendToAmazon = fbaStock > 0 && daysWithFBAStock <= displayThreshold;

              if (needToSendToAmazon) {
                canSendToAmazon = true;
                
                // Calculer les jours avant d'atteindre le niveau critique
                const daysBeforeCritical = daysWithFBAStock - criticalLevel;
                
                if (daysBeforeCritical <= 0) {
                  // Rouge : deadline dépassée
                  amazonSendOverdue = true;
                  amazonSendDate = new Date(today); // Aujourd'hui
                } else {
                  // Bleu : encore du temps avant la deadline
                  amazonSendOverdue = false;
                  amazonSendDate = new Date(today);
                  amazonSendDate.setDate(today.getDate() + daysBeforeCritical);
                }
              }
              
              // MODIFICATION: Calcul simplifié des jours avec stock à la maison
              // Plus de déduction pour la consommation pendant le transfert
              const daysWithHomeStock = dailySales > 0 && homeStock > 0
                ? Math.floor(homeStock / dailySales)
                : 0;
              
              // Calculer les jours restants avant l'arrivée du stock en transit
              const transitDaysRemaining = calculateTransitDaysRemaining(item);
              
              // Calculer le nombre de jours avec stock en transit
              let daysWithTransitStock = 0;
              if (dailySales > 0 && transitStock > 0) {
                daysWithTransitStock = Math.floor(transitStock / dailySales);
              }
              
              // MODIFICATION: Calcul simplifié du nombre total de jours de stock
              // Simplement la somme des jours de stock dans tous les emplacements
              const daysOfStock = daysWithCurrentStock + daysWithHomeStock + daysWithTransitStock;
                
              // Déterminer le délai de livraison du fournisseur pour ce produit
              let leadTime = 14; // Délai par défaut
              
              if (supplierConfig && supplierConfig.productSuppliers && supplierConfig.productSuppliers[sku]) {
                const supplierId = parseInt(supplierConfig.productSuppliers[sku]);
                const supplier = supplierConfig.suppliers?.find(s => s.id === supplierId);
                
                if (supplier) {
                  leadTime = supplier.leadTime;
                }
              }
              
              // Déterminer si un réapprovisionnement est nécessaire, en ignorant l'alerte si une commande est déjà en transit
              // eslint-disable-next-line no-unused-vars
              const needsReorder = daysOfStock <= (leadTime + safetyStock) && transitStock === 0;
                
              return (
                <tr key={rowIndex} className="stock-row">
                  <td>{item.sku}</td>
                  <td>{totalStock}</td>
                  <td>{item.fbmQuantity}</td>
                  <td>{item.fbaQuantity}</td>
                  
                  {/* Cellule de stock à la maison éditable */}
                  <td className="editable-cell">
                    {editingHomeStock === item.sku ? (
                      <div className="edit-mode">
                        <input
                          type="number"
                          value={tempHomeStock}
                          onChange={(e) => setTempHomeStock(e.target.value)}
                          min="0"
                          className="stock-input"
                        />
                        <button 
                          onClick={() => handleHomeStockSave(item.sku)}
                          className="save-btn"
                        >
                          ✓
                        </button>
                      </div>
                    ) : (
                      <div className="display-mode">
                        <span>{item.homeQuantity || 0}</span>
                        <button 
                          onClick={() => handleHomeStockEdit(item.sku)}
                          className="edit-btn"
                        >
                          ✎
                        </button>
                      </div>
                    )}
                  </td>
                  
                  {/* Cellule de stock en transit avec boutons Commande et Livré */}
                  <td className="editable-cell">
                    <div className="display-mode">
                      <span>{item.transitQuantity || 0}</span>
                      <div className="action-buttons">
                        <button 
                          onClick={() => handleTransitStockEdit(item.sku)}
                          className="edit-btn"
                          title="Modifier la commande"
                        >
                          ✎
                        </button>
                        {item.transitQuantity > 0 && (
                          <button 
                            onClick={() => handleTransitDeliveryOpen(item.sku)}
                            className="delivered-btn"
                            title="Marquer comme livré"
                          >
                            ✓
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  {/* Date de commande */}
                  <td>
                    {item.transitOrderDate ? new Date(item.transitOrderDate).toLocaleDateString() : '-'}
                  </td>
                  
                  {/* Jours avant arrivée */}
                  <td className={
                    transitDaysRemaining === 'Arrivé' ? 'positive' :
                    transitDaysRemaining <= 3 && transitDaysRemaining !== '-' && transitDaysRemaining !== 'Délai inconnu' ? 'warning' : ''
                  }>
                    {transitDaysRemaining}
                  </td>
                  
                  {/* Ventes par jour */}
                  <td>{parseFloat(item.dailySales).toFixed(2)}</td>
                  
                  {/* Jours de stock avec indications visuelles */}
                  <td className={`
                    ${(transitStock > 0) ? '' : 
                      (daysOfStock <= leadTime) ? 'critical' : 
                      (daysOfStock <= (leadTime + safetyStock)) ? 'warning' : ''}
                  `}>
                    {daysWithCurrentStock === 999 ? '∞' : daysOfStock}
                    {(daysWithHomeStock > 0 || daysWithTransitStock > 0) && (
                      <span className="stock-days-info">
                        {" "}({daysWithCurrentStock}
                        {daysWithHomeStock > 0 ? "+" + daysWithHomeStock : ""}
                        {daysWithTransitStock > 0 ? "+" + daysWithTransitStock : ""})
                      </span>
                    )}
                    {canSendToAmazon && (
                      <span className={amazonSendOverdue ? "amazon-send-overdue-info" : "amazon-send-info"}>
                        {amazonSendOverdue 
                          ? " ⚠️ Envoi à Amazon en retard" 
                          : " 📦 À envoyer à Amazon"}
                      </span>
                    )}
                  </td>
                  
                  {/* Nom du produit */}
                  <td>{item.productName ? (item.productName.length > 55 ? item.productName.substring(0, 55) + '...' : item.productName) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="table-summary">
        <p>Total: {filteredData.length} produits</p>
        <p>Stock FBM: {filteredData.reduce((sum, item) => sum + (parseInt(item.fbmQuantity) || 0), 0)} unités</p>
        <p>Stock FBA: {filteredData.reduce((sum, item) => sum + (parseInt(item.fbaQuantity) || 0), 0)} unités</p>
        <p>Stock FBA à la maison: {filteredData.reduce((sum, item) => sum + (parseInt(item.homeQuantity) || 0), 0)} unités</p>
        <p>Stock en transit: {filteredData.reduce((sum, item) => sum + (parseInt(item.transitQuantity) || 0), 0)} unités</p>
        <p>Stock Total: {filteredData.reduce((sum, item) => {
          return sum + 
            (parseInt(item.fbmQuantity) || 0) + 
            (parseInt(item.fbaQuantity) || 0) + 
            (parseInt(item.homeQuantity) || 0) + 
            (parseInt(item.transitQuantity) || 0);
        }, 0)} unités</p>
      </div>
      
      {/* Popup de commande (édition du stock en transit) */}
      {editingTransitStock && (
        <div className="supplier-select-overlay">
          <div className="supplier-select-popup">
            <h3>Détails de la commande en transit</h3>
            
            <div className="popup-field">
              <label>Quantité:</label>
              <input
                type="number"
                value={tempTransitStock}
                onChange={(e) => setTempTransitStock(e.target.value)}
                min="0"
                className="popup-input"
              />
            </div>
            
            <div className="popup-field">
              <label>Date de commande:</label>
              <input
                type="date"
                value={tempTransitDate}
                onChange={(e) => setTempTransitDate(e.target.value)}
                className="popup-input date-input"
              />
            </div>
            
            <div className="popup-field">
              <label>Fournisseur:</label>
              <select 
                value={tempSupplierId}
                onChange={(e) => setTempSupplierId(e.target.value)}
                className="supplier-select"
              >
                <option value="">Sélectionner un fournisseur</option>
                {supplierConfig && supplierConfig.suppliers && supplierConfig.suppliers.map((supplier) => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name} ({supplier.leadTime} jours)
                  </option>
                ))}
              </select>
            </div>
            
            <div className="supplier-select-buttons">
              <button onClick={() => setEditingTransitStock(null)} className="cancel-btn">
                Annuler
              </button>
              <button 
                onClick={() => handleTransitStockSave(editingTransitStock)}
                className="save-btn"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Popup de livraison */}
      {deliveringTransitStock && (
        <div className="supplier-select-overlay">
          <div className="supplier-select-popup">
            <h3>Marquer comme livré</h3>
            
            <div className="popup-field">
              <p>
                Comment souhaitez-vous traiter ce stock livré?
              </p>
              
              <div className="radio-option">
                <input
                  type="radio"
                  id="fba-home"
                  name="delivery-destination"
                  value="fba-home"
                  checked={deliveryDestination === 'fba-home'}
                  onChange={() => setDeliveryDestination('fba-home')}
                />
                <label htmlFor="fba-home">
                  Ajouter au stock FBA à la maison
                </label>
              </div>
              
              <div className="radio-option">
                <input
                  type="radio"
                  id="fbm-import"
                  name="delivery-destination"
                  value="fbm-import"
                  checked={deliveryDestination === 'fbm-import'}
                  onChange={() => setDeliveryDestination('fbm-import')}
                />
                <label htmlFor="fbm-import">
                  Stock FBM (sera mis à jour lors du prochain import)
                </label>
              </div>
            </div>
            
            <div className="supplier-select-buttons">
              <button onClick={() => setDeliveringTransitStock(null)} className="cancel-btn">
                Annuler
              </button>
              <button 
                onClick={() => handleTransitDeliveryComplete(deliveringTransitStock)}
                className="save-btn"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StockTable;